import styles from "./features.module.scss";

import { useEffect, useRef, useState, useMemo } from "react";
import { useRouter } from "next/router";
import CustomSpinner from "@/components/spinner/spinner";
import Head from "next/head";
import { svgImages } from "@/lib/svgImages";
import HeroGradientTextWithIcon from "@/components/heroGradientTextWithIcon/heroGradientTextWithIcon";
import Image from "next/image";
import TitleWithDescriptionV2 from "@/components/titleWithDescriptionV2/titleWithDescriptionV2";
import WhyChooseQrhubCardV2 from "@/components/whyChooseQrhubCardV2/whyChooseQrhubCardV2";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css/pagination";
import "swiper/css";
import "swiper/css/navigation";
import FaqWithCta from "@/components/faqWithCta/faqWithCta";
import { useWordPressApi, useWordPressData } from "@/hooks/useWordPressApi";

interface FeatureSection {
  feature_title: string;
  feature_subtitle: string;
  short_description: string;
  feature_description: string;
  coming_soon: boolean;
}

interface FeaturesData {
  acf: {
    title: string;
    description: string;
    hero_title: string;
    hero_description: string;
    features_sections: FeatureSection[];
  };
}

const Features = () => {
  const router = useRouter();
  const { getFeaturesData } = useWordPressApi();

  const {
    data: rawFeaturesData,
    loading,
    error,
  } = useWordPressData(() => getFeaturesData(), []);

  const featuresData = rawFeaturesData as FeaturesData | null;

  const [allImages, setAllImages] = useState<any>(null);
  const [imagesLoading, setImagesLoading] = useState(true);
  const [imagesError, setImagesError] = useState<string | null>(null);
  const swiperRef = useRef<SwiperType | null>(null);

  const currentUrl = router.asPath;
  const domainUrl =
    typeof window !== "undefined" ? window.location.hostname : "";
  useEffect(() => {
    const fetchImages = async () => {
      try {
        setImagesLoading(true);
        setImagesError(null);
        const response = await fetch("/images.json");
        if (!response.ok) throw new Error("Failed to fetch images");
        const data = await response.json();
        setAllImages(data);
      } catch (err) {
        setImagesError(
          err instanceof Error ? err.message : "Failed to fetch images",
        );
        console.error("Images fetch error:", err);
      } finally {
        setImagesLoading(false);
      }
    };

    fetchImages();
  }, []);

  const processedFeatures = useMemo(() => {
    if (!featuresData?.acf.features_sections) return [];

    return featuresData.acf.features_sections.map((item) => ({
      ...item,
      iconKey: item.feature_title
        .replace(/<br\s*\/?>/g, "")
        .replace(/\s/g, "_")
        .toLowerCase(),
      coming_soon: item.coming_soon,
    }));
  }, [featuresData]);

  const baseUrl = `https://${domainUrl || "qrhub.io"}`;
  const defaultOgImage = `${baseUrl}/images/featured_images/qrhub-featured.jpg`;
  const defaultOgUrl = `${baseUrl}${router.asPath || ""}`;

  return (
    <>
      <Head>
        <title>QRHUB - Features</title>
        <meta
          name="description"
          content="Discover QRHUB features for Dynamic and Static QR Code Solutions."
        />
        <meta property="og:title" content="QRHUB - Features" />
        <meta
          property="og:description"
          content="Discover QRHUB features for Dynamic and Static QR Code Solutions."
        />
        <meta property="og:image" content={defaultOgImage} />
        <meta property="og:url" content={defaultOgUrl} />
      </Head>
      {loading || imagesLoading ? (
        <CustomSpinner />
      ) : error ? (
        <div>Error loading features: {error}</div>
      ) : !featuresData ? (
        <div>No features data available</div>
      ) : (
        <>
          <Head>
            <title>QRHUB - {featuresData.acf.title}</title>
            <meta name="description" content={featuresData.acf.description} />
            <meta
              property="og:title"
              content={`QRHUB - ${featuresData.acf.title}`}
            />
            <meta
              property="og:description"
              content={featuresData.acf.description}
            />
            <meta property="og:image" content={defaultOgImage} />
            <meta property="og:url" content={currentUrl} />
          </Head>

          <div className={styles.features}>
            <HeroGradientTextWithIcon
              title={featuresData.acf.hero_title}
              subtitle="Dynamic QR"
              description={featuresData.acf.hero_description}
              image={
                <Image
                  src="/images/featuresPage/features.jpg"
                  width={311}
                  height={311}
                  alt="QRHUB Features"
                />
              }
              buttonTitle="Start for free"
              buttonUrl={process.env.NEXT_PUBLIC_DASHBOARD_URL || "#"}
              target="_blank"
              isProduct={false}
              isSolution={false}
              isStatic={false}
            />

            <div className="custom-container">
              <div className="inner">
                <div className={styles.featuresSwiperTitle}>
                  <TitleWithDescriptionV2
                    title="Why Choose <br/> QRHUB?"
                    description="No technical expertise required our step-by-step guide ensures anyone can generate and use QR codes effectively."
                    light={false}
                  />
                </div>

                {/* Desktop Cards */}
                <div className={`${styles.featuresCards} d-flex-md d-none-xs`}>
                  {processedFeatures.map((item, index) => (
                    <WhyChooseQrhubCardV2
                      key={`${item.feature_title}-${index}`}
                      title={item.feature_title}
                      subtitle={item.feature_subtitle}
                      shortDesc={item.short_description}
                      description={item.feature_description}
                      icon={svgImages[item.iconKey]}
                      featurePage={true}
                      comingSoon={item.coming_soon}
                    />
                  ))}
                </div>

                {/* Mobile Swiper */}
                <div
                  className={`${styles.featuresCardsMobile} d-none-md d-flex-xs`}
                >
                  <div className={styles.sliderContainer}>
                    <Swiper
                      modules={[Navigation, Autoplay, Pagination]}
                      centeredSlides={false}
                      loop={false}
                      navigation={{
                        nextEl: `.${styles.swiperButtonNext}`,
                        prevEl: `.${styles.swiperButtonPrev}`,
                      }}
                      autoplay={{
                        delay: 3000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true,
                        stopOnLastSlide: false,
                      }}
                      onSwiper={(swiper) => {
                        swiperRef.current = swiper;
                      }}
                      pagination={{
                        el: `.${styles.swiperPagination}`,
                        clickable: true,
                      }}
                      breakpoints={{
                        0: {
                          slidesPerView: 1.1,
                          spaceBetween: 10,
                          autoplay: {
                            disableOnInteraction: true,
                            pauseOnMouseEnter: false,
                          },
                        },
                        480: {
                          slidesPerView: 2,
                          spaceBetween: 20,
                        },
                        1025: {
                          slidesPerView: 3,
                          spaceBetween: 30,
                        },
                      }}
                      className={styles.featuresSwiperElement}
                      initialSlide={0}
                      grabCursor={true}
                    >
                      {processedFeatures.map((slide, index) => (
                        <SwiperSlide
                          key={`${slide.feature_title}-${index}`}
                          className={styles.swiperSlide}
                        >
                          <WhyChooseQrhubCardV2
                            title={slide.feature_title}
                            subtitle={slide.feature_subtitle}
                            shortDesc={slide.short_description}
                            description={slide.feature_description}
                            icon={svgImages[slide.iconKey]}
                            featurePage={true}
                            comingSoon={slide.coming_soon}
                          />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                    <div className={styles.swiperPagination}></div>
                  </div>
                </div>
              </div>
            </div>

            <FaqWithCta
              title="Ready to Get Started?"
              description="QRHUB can transform your business and connect you with your audience like never before."
              buttonTitle="Try for Free"
              buttonUrl={process.env.NEXT_PUBLIC_DASHBOARD_URL || "#"}
              target="_blank"
            />
          </div>
        </>
      )}
    </>
  );
};

export default Features;
